import React, { useMemo, useState } from 'react';
import { Button, Layout } from 'antd';
import MapListView from './components/MapListView';
import MapPortalHeader from './components/MapPortalHeader';
import Spinner from '../../shared/components/Spinner';
import useFetchAllMaps from '../../hooks/useFetchAllMaps';
import { PlusOutlined, GlobalOutlined } from '@ant-design/icons';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../routes/routeConstants/appRoutes';
import './MapPortal.scss';

const S3_BASE = 'https://map-portal-attachments.s3.ca-central-1.amazonaws.com/';

const getTextFromHtml = (html?: string) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').trim();
};

interface MapCardProps {
  title: string;
  thumbnail?: string;
  onClick: () => void;
}

const MapCard: React.FC<MapCardProps> = ({ title, thumbnail, onClick }) => {
  const hasThumb = !!thumbnail && thumbnail !== S3_BASE;
  const [errored, setErrored] = useState(false);
  const showImage = hasThumb && !errored;

  return (
    <button type="button" className="map-card" onClick={onClick}>
      <span className="map-card__title">{title || 'Untitled Map'}</span>
      <span className="map-card__thumb">
        {showImage ? (
          <img src={thumbnail} alt={title || 'Map thumbnail'} onError={() => setErrored(true)} />
        ) : (
          <span className="map-card__placeholder">
            <GlobalOutlined />
          </span>
        )}
      </span>
    </button>
  );
};

const MapPortal: React.FC = () => {
  const { data: maps, isLoading } = useFetchAllMaps();
  const navigate = useNavigate();
  const location = useLocation();

  const isMapView = location.hash !== '#list';

  const [sortBy, setSortBy] = useState<string>('Most Recent');

  const handleViewChange = (showMap: boolean) => {
    navigate(`#${showMap ? 'card' : 'list'}`);
  };

  const sortedMaps = useMemo(() => {
    if (!maps) return [];

    switch (sortBy) {
      case 'Creator':
        return [...maps].sort((a, b) => {
          const creatorA = a.permissions?.users?.[0]?.name || '';
          const creatorB = b.permissions?.users?.[0]?.name || '';
          return creatorA.localeCompare(creatorB);
        });
      case 'Map Title':
        return [...maps].sort((a: any, b: any) => {
          const titleA = getTextFromHtml(a.general?.mapTitle);
          const titleB = getTextFromHtml(b.general?.mapTitle);
          return titleA.localeCompare(titleB);
        });
      default:
        return maps; // Default order (Most Recent)
    }
  }, [maps, sortBy]);

  return (
    <Layout className="map-portal__layout">
      <Spinner isLoading={isLoading} />
      <MapPortalHeader title="MAP PORTAL" isMapView={isMapView} onChange={handleViewChange} onSortChange={setSortBy} />

      {isMapView ? (
        <div className="map-content">
          <div className="card-grid">
            {sortedMaps?.map((map: { id: any; general: { mapTitle: string; thumbnailImage: string } }) => (
              <MapCard
                key={String(map?.id)}
                title={getTextFromHtml(map?.general?.mapTitle)}
                thumbnail={map?.general?.thumbnailImage}
                onClick={() => navigate(generatePath(AppRoutes.MAP_VIEW, { id: String(map?.id) }))}
              />
            ))}
          </div>
        </div>
      ) : (
        <MapListView maps={sortedMaps} />
      )}

      <Button
        shape="circle"
        icon={<PlusOutlined className="plus-outlined" />}
        size="large"
        className="add-button"
        onClick={() => {
          navigate(generatePath(AppRoutes.MAP_VIEW, { id: 'new-map' }));
        }}
      />
    </Layout>
  );
};

export default MapPortal;
